import { tinaField } from "tinacms/dist/react";
import type { PagesQuery } from "@/tina/__generated__/types";
import { type Locale, type BlogPostItem, type ProductItem } from "@/lib/cms-server";
import Hero from "./Hero";
import MediaGalleryBlock from "./MediaGalleryBlock";
import RichTextBlock from "./RichTextBlock";
import Cta from "./Cta";
import FeatureGrid from "./FeatureGrid";
import Newsletter from "./Newsletter";
import FeaturedBlogPosts from "./FeaturedBlogPosts";
import ContactFormBlock from "./ContactFormBlock";
import BlogListingBlock from "./BlogListingBlock";
import ProductListingBlock from "./ProductListingBlock";

// Add a new block: create <Name>.template.tsx next to its render component
// (see Hero.template.tsx), add it to `pageBlocks` in
// tina/collections/pages.schema.tsx, then a case here mapping its
// __typename to the render component. Each block is wrapped with
// tinaField(block) (no field name = "edit this whole block") so it's
// click-to-edit in Tina's admin preview — a no-op outside that context,
// since tinaField() returns "" when the object has no live-edit metadata.
//
// Never mark a block-level (or block-list-item) field `required: true`,
// for two independent reasons, both confirmed live:
// 1. A required field left empty anyway (Tina's admin doesn't reliably
//    block the save even when a nested block field is marked required)
//    makes the GraphQL schema mark it non-null, and a null value on a
//    non-null field throws "Cannot return null for non-nullable field
//    ..." at query time — which PagesService.getBySlug's catch-all
//    swallows into a plain 404, with no error shown anywhere. A whole
//    page going dark because of one empty block field is worse than that
//    field just rendering blank, so every render component here already
//    guards each of its fields with `{data.field && (...)}` (matching how
//    already-optional fields like `subheading` were always handled) —
//    keep that pattern for any field you add.
// 2. Tina's query codegen merges same-named fields across sibling block
//    types within the `blocks` union (e.g. every block's own top-level
//    `heading`) into one shared shape, and errors the whole build if
//    their nullability doesn't match: "Fields \"heading\" conflict
//    because they return conflicting types \"String!\" and \"String\""
//    (confirmed by hitting it directly — see BlogListingBlock.template.tsx/
//    ContactFormBlock.template.tsx, which used to omit `heading` entirely
//    to dodge this). Consistently leaving every block field optional
//    avoids this class of conflict altogether, not just for `heading`.
//
// `locale`/`latestPosts`/`products`/`uiDictionary` are extra data blocks
// need but don't carry themselves (FeaturedBlogPosts/BlogListingBlock need
// blog posts, ProductListingBlock needs products, several blocks need
// translated UI-chrome fallback strings) — threaded down from the page
// component through PageView rather than fetched by the block itself,
// since blocks render inside PageView's client component tree and a CMS
// fetch (or a closure wrapping one) can't happen there — see
// cms/multilingual/translate-text.ts's comment on why `uiDictionary` is a
// plain resolved `{sourceText: translation}` map, not a live function.
// Newsletter and ContactFormBlock's field-level copy (labels/placeholders/
// button text) is optional on the block itself, falling back to the
// translated site default when unset — see NewsletterForm.tsx/ContactForm.tsx.
//
// `currentPage` is the same idea but comes from the URL (see
// app/[locale]/blog/listing.tsx and .../products/listing.tsx), not CMS
// content — only BlogListingBlock and ProductListingBlock's "all" mode
// read it, everything else ignores it.
//
// `blocks` is typed from PagesQuery directly rather than the generated
// `PagesBlocks` union — see ProductListingBlock.tsx's comment on
// `ProductListingData` for why: `PagesBlocks` (via `PagesBlocksProductListing`)
// types `manualProducts[].product` against the reusable single-document
// `Products` type, which isn't quite what a `reference` field nested inside
// this query actually resolves to. Deriving from PagesQuery matches what's
// really on the wire.
type Blocks = NonNullable<PagesQuery["pages"]["blocks"]>;

export default function BlocksRenderer({
  blocks,
  locale,
  latestPosts,
  products,
  currentPage = 1,
  uiDictionary,
}: {
  blocks: Blocks;
  locale: Locale;
  latestPosts: BlogPostItem[];
  products: ProductItem[];
  currentPage?: number;
  uiDictionary: Record<string, string>;
}) {
  return (
    <>
      {blocks.map((block, i) => {
        if (!block) return null;
        return (
          <div key={i} className="px-blocks-renderer" data-tina-field={tinaField(block)}>
            {block.__typename === "PagesBlocksHero" && <Hero data={block} />}
            {block.__typename === "PagesBlocksMediaGallery" && <MediaGalleryBlock data={block} />}
            {block.__typename === "PagesBlocksRichText" && <RichTextBlock data={block} />}
            {block.__typename === "PagesBlocksCta" && <Cta data={block} />}
            {block.__typename === "PagesBlocksFeatureGrid" && <FeatureGrid data={block} />}
            {block.__typename === "PagesBlocksNewsletter" && (
              <Newsletter data={block} uiDictionary={uiDictionary} />
            )}
            {block.__typename === "PagesBlocksFeaturedBlogPosts" && (
              <FeaturedBlogPosts data={block} posts={latestPosts} locale={locale} uiDictionary={uiDictionary} />
            )}
            {block.__typename === "PagesBlocksContactForm" && (
              <ContactFormBlock data={block} uiDictionary={uiDictionary} />
            )}
            {block.__typename === "PagesBlocksBlogListing" && (
              <BlogListingBlock
                data={block}
                posts={latestPosts}
                locale={locale}
                currentPage={currentPage}
                uiDictionary={uiDictionary}
              />
            )}
            {block.__typename === "PagesBlocksProductListing" && (
              <ProductListingBlock
                data={block}
                products={products}
                locale={locale}
                currentPage={currentPage}
                uiDictionary={uiDictionary}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
